import React from 'react';
import { useDispatch } from 'react-redux';
import { Polygon, Marker, MarkerIcon } from '@tcc/treadstone';
import L from 'leaflet';
import { openModal } from '../../../../actions/modalActions';
import { setSelectedActivity } from '../../../../actions/scoutingActions';
import Pin from '../../../../components/Map/Pin';
import { EnhancedPin } from '../../../../components/Map/EnhancedPin';
import { trackHeapEvent } from '../../../../actions/analyticsActions';

import styles from './ScoutingMap.css';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { Activity, FeatureFlagKey, isEnhancedActivity, ModalMode, ModalSize, ModalType, ShapeType } from '../../../../types';
import { useAppSelector } from '../../../../utils';
import { getActivitiesWithoutFieldNotes, getSelectedActivity } from '../../../../selectors';

export type ScoutingMapProps = {
  index: number;
};

export const ScoutingMap = ({ index }: ScoutingMapProps) => {
  const dispatch = useDispatch();
  const activities = useAppSelector(state => getActivitiesWithoutFieldNotes(state, { index }));
  const selected = useAppSelector(state => getSelectedActivity(state, { index }));
  const isScoutingRegionsVisible = useFeatureFlag(FeatureFlagKey.SCOUTING_REGIONS);
  const hasEnhancedScouting = useFeatureFlag(FeatureFlagKey.WEB_DMB_ENHANCED_SCOUTING)

  const onPolygonClick = (activity: Activity) => {
    dispatch(setSelectedActivity(index, activity.id));
    dispatch(openModal(index, null as never, {
      type: ModalType.REGION_REPORT,
      size: ModalSize.L,
      activity,
      mode: ModalMode.EDIT_REGION,
    }));
  };

  const onPointClick = (activity: Activity) => {
    dispatch(setSelectedActivity(index, activity.id));
    dispatch(openModal(index, null as never, {
      type: hasEnhancedScouting ? ModalType.ENHANCED_SCOUTING_FORM : ModalType.SCOUTING_FORM,
      edit: true,
      mode: hasEnhancedScouting ? ModalMode.SHOW_ENHANCED_PIN : ModalMode.SHOW_PIN,
      size: hasEnhancedScouting ? ModalSize.M : ModalSize.S,
      activity,
    }));
    dispatch(trackHeapEvent('View Scouting PIN'));
  };

  return (
    <div data-test-id="scouting-map">
      {activities.map((activity) => {
        const isSelected = activity.id === selected;
        switch (activity.location?.type) {
          case ShapeType.MULTI_POLYGON:
          case ShapeType.POLYGON:
            return (
              isScoutingRegionsVisible && (
                <Polygon
                  color="white"
                  data-test-id={`scouting-activity-polygon-${activity.id}`}
                  fillColor={activity.color}
                  fillOpacity={isSelected ? 0.5 : 0.3}
                  key={`scouting-activity-polygon-${activity.id}`}
                  onClick={() => onPolygonClick(activity)}
                  positions={activity.inversedLocation?.coordinates ?? [] as L.LatLngExpression[]}
                  weight={isSelected ? 4 : 1}
                />
              )
            );
          case ShapeType.POINT: {
            const isSelectedSizeNormal = isSelected ? 42 : 30;
            const isSelectedSizeEnhanced = isSelected ? 56 : 48;
            return (
              <Marker
                data-test-id={`scouting-activity-point-${activity.id}`}
                icon={
                  MarkerIcon({
                    iconSize: hasEnhancedScouting ? isSelectedSizeEnhanced : isSelectedSizeNormal,
                    className: hasEnhancedScouting ? styles.enhancedPin : styles.pin,
                    element: hasEnhancedScouting && isEnhancedActivity(activity)
                      ? <EnhancedPin activity={activity} />
                      : <Pin color={activity.color} />,
                  })
                }
                key={`scouting-activity-point-${activity.id}`}
                onClick={() => onPointClick(activity)}
                opacity={1}
                position={activity.inversedLocation?.coordinates ?? [] as L.LatLngExpression}
              />
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
};
